<template>
  <section
    class="step3-wrapper"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <button
      v-if="!isBulk && !isWidgetCreator && !isEditMode"
      class="close-icon"
      @click="toggleConfirmationModal"
    >
      <CloseIcon />
    </button>
    <div v-if="isDragging" class="upload-full-screen-zone">
      <DragIcon />
      <p>
        {{ $t("create.step2.videoLibraryFloatingBar.dragFiles") }}
      </p>
    </div>
    <!-- make this an isolated component -->
    <section class="formatStep">
      <div
        v-if="!currentVideo && !playerSelected"
        class="step-navbar"
        :class="{ 'bulk-step-navbar': isBulk }"
      >
        <div v-if="campaignHasNoVideos" class="no-videos-section">
          <div class="no-products-images">
            <img
              src="@/assets/young-women-computer.png"
              alt="young-women-computer"
            />
            <img
              src="@/assets/add-videos-below.png"
              alt="add-videos-below"
              class="absolute-image"
            />
          </div>
          <div class="no-products-text">
            <p class="main-text">No videos added</p>
            <p class="secondary-text">Videos will appear here</p>
          </div>
        </div>
        <section
          v-else
          class="video-cards"
          :class="
            ({ 'slide-from-right': isBackButtonClicked },
            { 'bulk-video-cards': isBulk || isWidgetCreator || isEditMode })
          "
        >
          <h1 v-if="!isBulk && !isWidgetCreator && !isEditMode">
            {{ $t("create.step3.title") }}
          </h1>
          <div
            v-if="!isBulk"
            class="general-player-button"
            :class="{ 'edit-general-player-button': isEditMode }"
            @click="playerSelected = true"
          >
            <div class="player-settings">
              <h3>{{ $t("create.step3.playerSettings") }}</h3>
              <p v-if="isDefaultSettings">
                {{ $t("create.step3.defaultSettings") }}
              </p>
              <p v-else>{{ $t("create.step3.customSettings") }}</p>
            </div>
            <img src="@/assets/arrow-next-simple.png" alt="arrow" />
          </div>

          <draggable
            v-model="campaign.videos"
            group="videos"
            :filter="'.isNonDraggable'"
            @change="changeOrder($event)"
          >
            <video-card
              v-for="(video, index) in videosToIterate"
              :key="video._id"
              :class="{
                'bulk-video-card': isBulk,
                isNonDraggable: nonDraggable(video),
              }"
              :video="video"
              :videos="campaign.videos"
              :img="video.thumbnail"
              :is-portrait="video.height > video.width"
              :index="index"
              :cta="video.cta"
              :is-bulk="isBulk"
              :is-carousel="isCarousel"
              :is-full-screen="isFullScreen"
              :is-main-grouping="video.isMainGrouping"
              :thumbnail-grouping="video.thumbnailGrouping"
              :is-not-current-feed="isNotCurrentFeed(video)"
              @click="currentVideo = { video, index }"
              @delete="deleteVideo"
              @group-thumbnail="openGroupingThumbnailModal(video)"
              @disable-thumbnail="disableMainThumbnail(video)"
              @change-index="handleChangeIndex"
              @replace-video="openReplaceVideoModal"
            ></video-card>
          </draggable>
        </section>
        <div
          v-if="!isWidgetCreator"
          class="settings-footer"
          :class="[
            { 'edit-bulk-settings-footer-wrapper': isEditMode && isBulk },
            { 'edit-settings-footer-wrapper': isEditMode && !isBulk },
          ]"
        >
          <edit-footer
            v-if="isEditMode && !isBulk"
            :is-video-library-open="openVideoLibrary"
            @prev="editGoBack"
            @next="goNextStep"
          />
          <bulk-embed-confirm
            v-else-if="isBulk"
            :is-video-library-open="openVideoLibrary"
            :is-step3="true"
            :is-product-pages="isProductPages"
            @confirm="goNextStep"
            @cancel="$emit('close-elements-modal')"
          />
          <settings-footer v-else @prev="goPreviousStep" @next="goNextStep" />
        </div>
      </div>
      <!-- START SETTINGS -->
      <div
        v-else-if="playerSelected"
        class="settings"
        :class="{ 'bulk-settings': isBulk }"
      >
        <div
          v-if="!isBulk"
          class="player-title-wrapper"
          :class="[
            {
              'widget-creator-title-wrapper': isWidgetCreator,
            },
            { 'edit-mode-title-wrapper': isEditMode },
          ]"
        >
          <button
            v-if="isWidgetCreator"
            class="back-button"
            :class="{
              'widget-creator-back-button': isWidgetCreator || isEditMode,
            }"
            @click="deselectPlayer(false)"
          >
            <ArrowBackIcon />
            <p>{{ $t("shared.buttons.back") }}</p>
          </button>
          <h2 class="step-title title-bolder">
            {{ $t("create.step3.playerTitle") }}
          </h2>
        </div>
        <CampaignFormatSettings
          v-if="campaignLoaded"
          class="slide-from-left"
          :class="{ 'bulk-slide-from-left': isBulk }"
          :is-bulk="isBulk"
          :campaign="campaign"
          :desktop="desktop"
          :mobile="mobile"
          :chosen-device="chosenDevice"
          :is-carousel="isCarousel"
          :is-embed="isEmbed"
          :campaign-allowed="campaignAllowedOn"
          :current-plan="currentPlan"
          :text-options="currentVideoTextOptions"
          :is-widget-creator="isWidgetCreator"
          :is-shared-format="sharedFormat"
          :is-edit-bulk-format="isEditBulkFormat"
          @update-device="updateDevice"
          @format-object-change="changeFormatObject"
          @video-title-change="updateVideoTitle"
          @stickybar-object-change="changeStickyBarObject"
          @format-type-change="handleFormatTypeChange"
          @fill-object-if-empty="fillObjectIfEmpty"
          @change-cta-object="changeCtaObject($event, 0)"
          @change-image="changeThumbnail($event, 0)"
          @previous-step="goPreviousStep"
          @next-step="goNextStep"
          @edit-go-back="editGoBack"
          @border-color-changed="handleBorderColorChanged"
        ></CampaignFormatSettings>
        <div class="bulk-format-footer-settings">
          <bulk-embed-confirm
            v-if="isBulk"
            @confirm="goNextStep"
            @cancel="$emit('close-elements-modal')"
          />
        </div>
        <div
          v-if="isWidgetCreator"
          class="done-button-wrapper"
          :class="{
            'widget-creator-done-button': isWidgetCreator,
            'edit-mode-done-button': isEditMode,
          }"
        >
          <div class="done-button" @click="deselectPlayer(true)">
            {{ $t("shared.buttons.done") }} 👌
          </div>
        </div>
      </div>
      <div
        v-else-if="currentVideo"
        class="cta-navbar"
        :class="{ 'bulk-cta-navbar': isBulk }"
      >
        <div
          class="title-wrapper"
          :class="{ 'bulk-title-wrapper': isBulk || isEditMode }"
        >
          <button
            v-if="isWidgetCreator"
            class="back-button"
            :class="{
              'widget-creator-back-button': isWidgetCreator || isEditMode,
            }"
            @click="deselectVideo(false, null)"
          >
            <ArrowBackIcon />
            <p>{{ $t("shared.buttons.back") }}</p>
          </button>
          <h1 class="step-title title-bolder">
            {{ $t("create.step3.selectElement") }}
          </h1>
        </div>
        <div class="cta-wrapper">
          <cta-block
            class="slide-from-left"
            :class="{ 'bulk-slide-from-left': isBulk }"
            :campaign-cta-option="campaign.videos[currentVideo.index].cta"
            :chosen-device="chosenDevice"
            :show-cta-button="campaign.videos && campaign.videos.length > 1"
            :products="currentVideoCta.products"
            :is-full-screen="isFullScreen"
            :is-bulk="isBulk"
            :is-edit="isEditMode"
            :campaign="campaign"
            :video-index="currentVideo.index"
            :current-feed-edit-index="currentFeedEditIndex"
            @change-cta-object="changeCtaObject($event)"
            @open-add-to-cart="showAddToCartModal = true"
            @copy-cta-button-clicked="copyCtaForAllVideos($event)"
            @product-order-change="handleProductOrderChange"
            @remove-product="removeProduct"
          ></cta-block>
          <thumbnail-selector
            v-if="isCarousel || isEmbed"
            class="slide-from-left"
            :has-thumbnail="
              Boolean(campaign.videos[currentVideo.index].thumbnail)
            "
            :video="videoToPreview.videos[0]"
            :class="[{ 'bulk-slide-from-left': isBulk }]"
            @upload-image="changeThumbnail($event)"
          ></thumbnail-selector>
          <CarouselTextSettings
            v-if="isCarousel || isEmbed"
            :text-options="currentVideoTextOptions"
            :is-story="isStory"
            :is-bulk="isBulk"
            :current-index="currentVideo.index"
            :current-video="currentVideo.video"
            :current-feed-edit-index="currentFeedEditIndex"
            class="slide-from-left"
            :class="{ 'bulk-slide-from-left': isBulk }"
            @change-text-object="changeText"
          ></CarouselTextSettings>
        </div>
        <div
          v-if="isWidgetCreator"
          class="done-button-wrapper"
          :class="{
            'bulk-done-button': isBulk || isEditMode,
            'product-pages-done-button': isProductPages,
            'widget-creator-done-button': isWidgetCreator,
          }"
        >
          <div
            class="done-button"
            @click="deselectVideo(true, campaign.videos[currentVideo.index])"
          >
            {{ $t("shared.buttons.done") }} 👌
          </div>
        </div>
      </div>

      <vidjet-preview
        v-if="
          (!(openVideoLibrary && selectedVideo) &&
            (currentVideo || videoToPreview.videos.length) &&
            campaignLoaded) ||
          campaignHasNoVideos
        "
        :key="
          isBubble
            ? campaign.videos.map((video) => video._id).join('-')
            : 'static-preview'
        "
        :is-embed="isEmbed"
        :class="{ 'bulk-preview': isBulk }"
        :is-carousel="isCarousel"
        :preview-campaign="videoToPreview"
        :chosen-device="!playerSelected ? getAllowedCampaign : chosenDevice"
        :embed-thumbnail="embedThumbnail"
        :desktop="desktop"
        :mobile="mobile"
        :cta-preview="currentVideoCta"
        :text-options-preview="currentVideoTextOptions"
        :campaign-type="campaign.campaignType"
        :hide-video="playerSelected && !campaignAllowedOn[chosenDevice]"
        :is-edit-mode="isEditMode"
        :is-desktop-toggled="isDesktopToggled"
        :progress-bar-step="2"
        :is-mobile-toggled="isMobileToggled"
        :all-videos="videosToIterate"
        :show-preview="showPreview"
        :is-product-pages="isProductPages"
        :is-bulk="isBulk"
        :campaign-has-no-videos="campaignHasNoVideos"
        :is-widget-creator="isWidgetCreator"
        @add-product="userDontNeedProducts()"
        @fullscreen-toggle="handleFullscreenToggle"
      />
      <vidjet-preview
        v-if="openVideoLibrary && selectedVideo"
        :is-step2="true"
        :is-embed="true"
        :is-video-library-preview="true"
        :preview-campaign="selectedVideo"
        :is-widget-creator="isWidgetCreator"
      ></vidjet-preview>
      <small-modal v-if="showSignupModal" @close="toggleSignupModal"
        ><signup-login
          :is-modal="true"
          @login-done="showSignupModal = false"
          @signup-done="showSignupModal = false"
      /></small-modal>
      <AddToCartModal
        v-if="showAddToCartModal && currentVideo"
        :is-full-screen="isFullScreen"
        :selected-products-edit="
          currentVideoCta.products && currentVideoCta.products.length > 0
            ? currentVideoCta.products
            : false
        "
        :products-loading="productsLoading"
        @close-add-to-cart="showAddToCartModal = false"
        @submit-add-to-cart-products="submitAddToCartProducts"
      />
      <div
        v-if="playerSelected && !isBulk && !isWidgetCreator"
        class="absolute-position"
      >
        <button
          class="cancel-button flex-button"
          :class="{
            'widget-creator-back-button': isWidgetCreator || isEditMode,
          }"
          @click="deselectPlayer(false)"
        >
          <ArrowNext class="arrow-back" />
          <p>{{ $t("shared.buttons.back") }}</p>
        </button>

        <button class="save-button flex-button" @click="deselectPlayer(true)">
          {{ $t("shared.buttons.done") }}
          <SquaredCheckIcon class="button-icon" />
        </button>
      </div>
      <div
        v-if="currentVideo && !isWidgetCreator"
        class="absolute-position"
        :class="[
          { 'bulk-absolute-position': isBulk },
          { 'product-pages-absolute-position': isProductPages },
        ]"
      >
        <button
          class="cancel-button flex-button"
          :class="{
            'widget-creator-back-button': isWidgetCreator || isEditMode,
          }"
          @click="deselectVideo(false, null)"
        >
          <ArrowNext class="arrow-back" />
          <p>{{ $t("shared.buttons.back") }}</p>
        </button>

        <button
          class="save-button flex-button"
          @click="deselectVideo(true, campaign.videos[currentVideo.index])"
        >
          {{ $t("shared.buttons.done") }}
          <SquaredCheckIcon class="button-icon" />
        </button>
      </div>
    </section>
    <small-modal
      v-if="showConfirmationModal"
      :fit-content="true"
      @close="toggleConfirmationModal"
    >
      <div class="confirmation-modal-content">
        <h1 class="confirmation-modal-title">
          {{ $t("create.step3.leaveModal.title") }}
        </h1>
        <h2 class="confirmation-modal-subtitle">
          {{ $t("create.step3.leaveModal.subtitle") }}
        </h2>
        <banner-modal
          banner-type="info"
          :title="$t('deleteCampaign.information')"
          :text="$t('create.step3.leaveModal.bannerInfo')"
        >
          <template #icon>
            <InfoIcon />
          </template>
        </banner-modal>
        <div class="confirmation-modal-buttons">
          <button
            class="button-primary back-button"
            @click="toggleConfirmationModal"
          >
            {{ $t("create.step3.leaveModal.cancel") }}
          </button>
          <button class="button-primary" @click="leaveCampaignCreator">
            {{ $t("create.step3.leaveModal.confirm") }}
          </button>
        </div>
      </div>
    </small-modal>
    <GroupingThumbnailModal
      v-if="showGroupingThumbnailModal"
      :video="videoToGroupInThumbnail"
      @group-thumbnail="setAsMainThumbnail"
      @close="showGroupingThumbnailModal = false"
    />
    <FloatingUploadingBar
      v-if="!isEditBulkFormat"
      :files-to-upload="filesToUpload"
      @open-video-library="openVideoLibrary = true"
      @push-files="pushFiles"
    />
    <sliding-modal
      v-if="openVideoLibrary"
      class="video-library-modal"
      :slide-direction="slideDirection"
      @close="closeVideoLibrary"
    >
      <VideoLibrary
        :compact="true"
        @play-video-in-preview="selectVideoInPreview"
        @import-videos="importVideosFromLibrary"
        @close-library="closeVideoLibrary"
        @reset-selected-video="resetSelectedVideo"
      />
    </sliding-modal>
    <EmbedCodeModal
      v-if="showEmbedCodeModal"
      :campaign="campaign"
      @close="closeEmbedCodeModal"
    />
    <ReplaceVideoModal
      v-if="showReplaceVideoModal"
      :index="videoIndexToReplace"
      :video="videoToReplace"
      @close="closeReplaceVideoModal"
      @replace-video="replaceVideo"
    />
  </section>
</template>
<script>
/*
  SCRIPT OVERVIEW

  This component is most important part for campaign creation/editing flow.
  It manages:
  - The list of videos in the campaign (add/import/reorder/delete/replace)
  - Per-video CTA and text configuration
  - Global "player settings" (format per device, triggers, etc.)
  - Live preview on desktop/mobile
  - Bulk mode (feeds): restrict reordering/deleting when videos belong to other feeds
  - Product Pages mode: can merge videos coming from product-attached feeds
  - Widget Creator mode: simplified flow, closes settings with "Done"

  State comes from:
  - Vuex: campaignCreation / campaign single / video library / site products etc.
  - Route query/params to infer initial goals/types
*/

import { mapGetters, mapState } from "vuex";

import CampaignFormatSettings from "./step3/CampaignFormatSettings.vue";
import VidjetPreview from "../shared/Vidjet-preview.vue";
import SettingsFooter from "@/components/create-campaign-steps/SettingsFooter";
import SmallModal from "@/components/shared/SmallModal";
import EditFooter from "@/components/create-campaign-steps/EditFooter";
import SignupLogin from "@/components/shared/SignupLogin";
import enums from "../../enums";
import CtaBlock from "./step3/CtaBlock.vue";
import AddToCartModal from "./step3/AddToCartModal.vue";
import SlidingModal from "@/components/shared/SlidingModal.vue";
import VideoLibrary from "@/components/create-campaign-steps/VideoLibrary.vue";
import { generateVideoCover } from "@/utils/generate-video-cover";
import ReplaceVideoModal from "./step3/ReplaceVideoModal.vue";

import campaignCreationMixin from "./campaignCreationMixin";
import VideoCard from "./step3/VideoCard.vue";
import ArrowBackIcon from "@/assets/svg/arrow-back.svg?inline";
import Draggable from "vuedraggable";
import ThumbnailSelector from "./step3/ThumbnailSelector.vue";
import { uploadThumbnailForVideo } from "../../utils/uploadToAmplify";
import BannerModal from "@/components/shared/BannerModal.vue";
import InfoIcon from "@/assets/svg/banner-info-icon.svg?inline";
import CarouselTextSettings from "./step3/CarouselTextSettings.vue";
import CloseIcon from "@/assets/svg/settings/close.svg?inline";
import BulkEmbedConfirm from "./BulkEmbedConfirm.vue";
import GroupingThumbnailModal from "@/components/create-campaign-steps/step3/GroupingThumbnailModal.vue";
import FloatingUploadingBar from "@/components/FloatingUploadingBar.vue";
import EmbedCodeModal from "@/components/campaign-manager/EmbedCodeModal.vue";
import SquaredCheckIcon from "@/assets/svg/squared-check-icon.svg?inline";
import ArrowNext from "@/assets/svg/arrow-next.svg?inline";
import DragIcon from "@/assets/svg/drag-video-icon.svg?inline";
const { FormatType, Position, DeviceType, CtaType, CtaAction } = enums;

export default {
  name: "CreateCampaignStep3",
  components: {
    CloseIcon,
    VidjetPreview,
    SettingsFooter,
    EditFooter,
    SignupLogin,
    SmallModal,
    CtaBlock,
    AddToCartModal,
    VideoCard,
    ArrowBackIcon,
    Draggable,
    CampaignFormatSettings,
    ThumbnailSelector,
    BannerModal,
    InfoIcon,
    CarouselTextSettings,
    BulkEmbedConfirm,
    GroupingThumbnailModal,
    FloatingUploadingBar,
    SlidingModal,
    VideoLibrary,
    EmbedCodeModal,
    ReplaceVideoModal,
    SquaredCheckIcon,
    ArrowNext,
    DragIcon,
  },
  mixins: [campaignCreationMixin],
  beforeRouteEnter(t, f, next) {
    // Initialize when entering this route (creation mode)
    next(async (vm) => {
      vm.init();
    });
  },
  props: {
    // EDIT MODE toggles: allows editing an existing campaign rather than creating one
    isEditMode: {
      type: Boolean,
      default: false,
    },
    // BULK mode: editing a feed from a bulk campaign (segmented by product/language/url)
    isBulk: {
      type: Boolean,
      default: false,
    },
    // Bulk "format-only" edit: when true, opens player settings directly (no videos view)
    isEditBulkFormat: {
      type: Boolean,
      default: false,
    },
    // Index of the feed being edited in bulk
    currentFeedEditIndex: {
      type: Number,
      default: null,
    },
    // Product Pages context flag
    isProductPages: {
      type: Boolean,
      default: false,
    },
    // Campaign id passed from Product Pages when jumping into step 3
    campaignIdFromProductPages: {
      type: String,
      default: null,
    },
    // Product id (or key) currently targeted in Product Pages
    selectedProductId: {
      type: [Number, String],
      default: null,
    },
    // True when user came from Step 1 (to enforce some UI rules)
    comesFromStepOne: {
      type: Boolean,
      default: false,
    },
    // Widget Creator context
    isWidgetCreator: {
      type: Boolean,
      default: false,
    },
    // If Step 3 is opened from Product Pages, we can receive videos already attached to product
    videosAttachedOnProduct: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // UI state: chosen device tab for player settings
      chosenDevice: "desktop",
      // For embed/carousel preview
      embedThumbnail: "",
      // Which device(s) are enabled for the campaign
      campaignAllowedOn: {
        desktop: true,
        mobile: true,
      },
      // Modals & flags
      showAddToCartModal: false,
      showSignupModal: false,
      isLoading: false,

      // Device tabs
      options: ["desktop", "mobile"],

      // Default format objects used as base for new formats
      defaultEmbedProperties: {
        formatType: 2,
        responsive: true,
        animatedThumbnail: "none",
        embedSize: {
          height: 0,
          width: 0,
        },
      },
      defaultStickyBarProperties: {
        formatType: 0,
        moveY: 0,
        isStickyBar: true,
        stickyBarOptions: {
          text: "Watch Video",
          textColor: "#FFFFFF",
          backgroundColor: "#7B01B5",
        },
      },
      defaultBubbleProperties: {
        formatType: 1,
        isStickyBar: false,
        openByDefault: false,
        position: 0,
        bubbleSize: 90,
        bubbleShape: "round",
        moveX: 0,
        moveY: 0,
        bubbleBorder: false,
        bubbleBorderColor: "#000000",
        isFullScreen: false,
        hideThumbnailPlay: true,
      },

      // Current format objects for desktop/mobile (populated later)
      desktop: {},
      mobile: {},

      // Base properties shared among formats (merged onto device objects)
      baseFormatObject: {
        autoLoop: false,
        autoScroll: false,
        hidePlayPause: true,
        hideThumbnailPlay: false,
        hideControlBar: this.isFullScreen ? false : true,
        title: "",
        hideFeedArrow: false,
        isHorizontalSwipe: false,
        hasProductDescription: false,
        hideProductCards: false,
        iconSettings: {
          iconType: "minimal",
          animation: "none",
          secondaryColor: "#c4c4c4",
          color: "white",
          opacity: 1,
        },
        arrowSettings: {
          iconType: "corner1",
          secondaryColor: "#A3A3A3",
          color: "black",
          opacity: 1,
        },
        cornerShape: 10,
        position: 2,
        quickShop: {
          isActive: false,
          productTextColor: "#FFFFFF",
          productBackgroundColor: "#000000",
          buttonTextColor: "#FFFFFF",
          buttonBackgroundColor: "#000000",
          ctaAction: CtaAction.addtoCart,
        },
        cta: {
          textColor: "#FFFFFF",
          buttonColor: "#000000",
          ctaAction: CtaAction.buyNow,
          buttonShape: "semi",
        },
        thumbnailBorders: {
          isActive: false,
          color: "#000000",
          size: "2",
        },
        thumbnailShadow: false,
        textOptions: {},
      },

      // Enum shortcuts for template / methods
      FormatType,
      DeviceType,
      Position,
      CtaType,

      // Campaign model (videos array is central to this step)
      campaign: { videos: [] },

      // Current selected video (object + index) for per-video editor
      currentVideo: null,

      // True when player settings are open
      playerSelected: false,

      // Snapshot of campaignCreation state (when not in edit mode)
      campaignCreation: {},

      // Per-video CTA and text options mirrors (to avoid direct nested reactivity issues)
      currentVideoCta: {},
      currentVideoTextOptions: {},

      // Progress bar for uploads when creating (step 2 -> 3)
      showUploadProgress: false,

      // Show/hide live preview
      showPreview: true,

      // Leave confirmation modal
      showConfirmationModal: false,

      // CSS transition helper
      isBackButtonClicked: false,

      // Preview fullscreen state
      isCurrentlyFullscreen: false,

      // Thumbnail grouping modal state
      showGroupingThumbnailModal: false,
      hasGroupedThumbnails: false,
      videoToGroupInThumbnail: null,

      // Flag used to avoid flicker when hydrating content
      loadContent: false,

      // Step 2 library modal & preview
      openVideoLibrary: false,
      showImporterModal: false,
      selectedVideo: null,
      slideDirection: "right",

      // Track new videos added in this session (used to tag product ids after save)
      newVideosAdded: null,

      // Embed code modal for carousel/embed
      showEmbedCodeModal: false,

      // In edit mode, list of products targeted by segmentation (used to pre-tag CTAs)
      productsInSegmentation: [],

      // Upload counters (optional UI)
      numberOfVideosToUpload: 0,

      // ATC product search loading
      productsLoading: false,

      // Replace video modal state
      showReplaceVideoModal: false,
      videoIndexToReplace: null,
      videoToReplace: null,

      // Data source for v-for in cards (merged in product pages mode)
      videosToIterate: [],

      // If opening from Product Pages, we can show extra videos from other feeds
      otherVideosAttachedOnProduct: [],

      // Drag & drop files payload
      filesToUpload: null,

      // Drag state for overlay
      isDragging: false,

      // True when campaign has a single format reused across devices
      sharedFormat: false,
    };
  },

  computed: {
    ...mapState({
      products: (state) => state.product.products,
    }),
    ...mapGetters({
      getCampaignCreation: "campaign/campaignCreation",
      singleCampaign: "campaign/getSingle",
      getSiteId: "account/getSiteId",
      getIsLoggedIn: "account/getIsLoggedIn",
      currentPlan: "plan/currentPlan",
      currentFeedEdit: "campaign/currentFeedEdit",
      feedList: "campaign/feedList",
      getVideoLibrary: "video/getVideoLibrary",
      isUsingProductKey: "site/isUsingProductKey",
      getUploadVideos: "video/getUploadVideos",
    }),

    // Toggles used by preview header icons
    isDesktopToggled() {
      return this.campaignAllowedOn.desktop;
    },
    isMobileToggled() {
      return this.campaignAllowedOn.mobile;
    },

    // Helper flags for current selected format object
    isStickyBar() {
      return this.selectedFormatObject.isStickyBar;
    },
    isStory() {
      return this.selectedFormatObject.isStory;
    },
    isEmbed() {
      return this.selectedFormatObject.formatType === 2;
    },
    isBubble() {
      return this.selectedFormatObject.formatType === 1;
    },
    isPopup() {
      return this.selectedFormatObject.formatType === 0;
    },

    // Fullscreen if any device format has it
    isFullScreen() {
      return this.desktop.isFullScreen || this.mobile.isFullScreen;
    },

    // Carousel flag, checking both devices
    isCarousel() {
      if (Object.keys(this.desktop ?? {}).length > 0) {
        return (
          this.desktop.formatType === FormatType.carousel ||
          this.mobile.formatType === FormatType.carousel
        );
      } else {
        return false;
      }
    },

    // True when campaign object is initialized
    campaignLoaded() {
      return Object.keys(this.campaign).length > 0;
    },

    // Current device object (desktop/mobile)
    selectedFormatObject() {
      return this.chosenDevice === "desktop" ? this.desktop : this.mobile;
    },

    // Campaign payload for preview component
    // If a video is selected, preview only that one with its CTA/text/thumbnail.
    // If not, preview full list (and ensure grouping fields exist if grouping is enabled).
    videoToPreview() {
      if (this.currentVideo && this.campaign.videos) {
        let previewVideo = this.currentVideo.video;
        return {
          videos: [
            {
              ...previewVideo,
              cta: this.currentVideoCta ?? {},
              thumbnail: previewVideo.thumbnail,
              tempThumbnailFile: previewVideo.thumbnail,
              textOptions: this.currentVideoTextOptions,
            },
          ],
        };
      }

      const videosToPreview = structuredClone(this.videosToIterate);
      const hasMainGrouping = videosToPreview.some(
        (video) => video.isMainGrouping
      );

      // When grouping is enabled, ensure each video has a thumbnailGrouping label
      if (hasMainGrouping) {
        let thumbnailIndex = 0;

        for (const videoObj of videosToPreview) {
          if (!videoObj.isMainGrouping && !videoObj.thumbnailGrouping) {
            thumbnailIndex++;
            videoObj.isMainGrouping = true;
            videoObj.thumbnailGrouping = `thumbnail ${thumbnailIndex}`;
          } else if (videoObj.isMainGrouping) {
            thumbnailIndex++;
            videoObj.thumbnailGrouping = `thumbnail ${thumbnailIndex}`;
          } else {
            videoObj.thumbnailGrouping = `thumbnail ${thumbnailIndex}`;
          }
        }
      }
      return { videos: videosToPreview, _id: this.campaign._id };
    },

    // Whether current device formats equal defaults (for UI messaging)
    isDefaultSettings() {
      return (
        JSON.stringify(
          this.getDefaultFormatProperties("desktop", this.$route.query.goal)
        ) == JSON.stringify(this.desktop) &&
        JSON.stringify(
          this.getDefaultFormatProperties("mobile", this.$route.query.goal)
        ) == JSON.stringify(this.mobile)
      );
    },

    // Helper for preview device selection when only one device is enabled
    getAllowedCampaign() {
      if (this.campaignAllowedOn.desktop) return "desktop";
      if (this.campaignAllowedOn.mobile) return "mobile";
      return null;
    },

    // Find videos with >1 product in CTA (used for specific UI)
    videosWithMoreProducts() {
      return this.campaign.videos.filter(
        (video) => video.cta?.products?.length > 1
      );
    },

    // Optional states used to adapt UI footers/empty states
    isProductPagesWithNoVideos() {
      return this.isProductPages && this.campaign.videos.length === 0;
    },
    isBulkWithNoVideos() {
      return this.isBulk && this.campaign.videos.length === 0;
    },

    // True when there’s nothing to show in cards
    campaignHasNoVideos() {
      return !this.videosToIterate?.length;
      // return this.campaign?.videos.length === 0;
    },

    // Default CTA object auto-filled when user creates a new video CTA in bulk contexts
    defaultCtaObject() {
      const isATC = (v) => v?.cta?.ctaType === CtaType.addToCart;

      if (this.isBulk) {
        const ctaInCurrentFeed = this.currentFeedEdit?.videos?.find(isATC)?.cta;
        if (ctaInCurrentFeed) return structuredClone(ctaInCurrentFeed);

        const feeds = Array.isArray(this.feedList) ? this.feedList : [];
        for (const feed of feeds) {
          const videoWithCta = (feed?.videos || []).find(isATC);
          if (videoWithCta?.cta) return structuredClone(videoWithCta.cta);
        }
      } else {
        const ctaInCampaign = this.campaign?.videos?.find(isATC)?.cta;
        if (ctaInCampaign) return structuredClone(ctaInCampaign);
      }

      return {
        textColor: "#000000",
        buttonColor: "#E5E5E5",
        ctaType: 3,
        ctaAction: "buyNow",
        bottomMarginPercentage: 10,
        buttonShape: "semi",
        productIds: [],
      };
    },

    // Local helper for syncing campaignCreation->videos in creation mode
    localVideos() {
      if (!this.isEditMode) {
        return [...this.campaign.videos];
      } else return [];
    },

    // Whether any video is marked as main grouping (carousel thumbnail grouping)
    hasMainGrouping() {
      return this.campaign.videos.some((video) => video.isMainGrouping);
    },

    // If any modal is open, suppress some interactions like drag/drop overlay
    hasAnyModalOpen() {
      return (
        this.showReplaceVideoModal ||
        this.openVideoLibrary ||
        this.showSignupModal ||
        this.showImporterModal ||
        this.showConfirmationModal ||
        this.showEmbedCodeModal
      );
    },

    // Determine text options for the current format (fallback to first video textOptions)
    computedTextOptions() {
      const hasFormatText =
        this.selectedFormatObject?.textOptions &&
        Object.keys(this.selectedFormatObject.textOptions).length > 0;

      const textOptions = hasFormatText
        ? this.selectedFormatObject.textOptions
        : this.campaign.bulk?.[0]?.videos?.[0]?.textOptions ||
          this.campaign.videos?.[0]?.textOptions ||
          {};

      return textOptions;
    },

    // For bubble/popup in bulk format edit, saving trigger together with formats
    shouldSaveTrigger() {
      return (
        this.isBulk && this.isEditBulkFormat && (this.isBubble || this.isPopup)
      );
    },
  },

  watch: {
    // When user selects a video, load its text options mirror
    currentVideo() {
      if (this.currentVideo) {
        this.currentVideoTextOptions =
          this.campaign.videos[this.currentVideo.index].textOptions;
      } else {
        this.currentVideoTextOptions = {};
      }
    },

    // If the number of videos changes while in carousel, recompute grouping
    "campaign.videos.length"(newLength, oldLength) {
      if (this.isCarousel) {
        if (newLength !== oldLength) {
          this.updateThumbnailGrouping();
        }
      }
    },

    // When campaign.videos changes in Product Pages mode, merge with external list and dedupe
    "campaign.videos"() {
      if (this.isProductPages) {
        this.videosToIterate = [
          ...this.campaign.videos,
          ...this.otherVideosAttachedOnProduct,
        ].filter(
          ((video) => {
            //Hotfix: dedupe by _id when combining "unique product feed" videos and product-attached ones
            const seen = new Set();
            return function (v) {
              if (seen.has(v._id)) return false;
              seen.add(v._id);
              return true;
            };
          })()
        );
      } else this.videosToIterate = this.campaign.videos;
    },

    // In creation mode, sync local changes into campaignCreation in store
    localVideos: {
      handler(newVideos) {
        if (newVideos) {
          this.$store.commit("campaign/campaignCreationSetVideos", newVideos);
        }
      },
      deep: true,
    },

    // Bubble up openVideoLibrary changes to parent (used by floating bar)
    openVideoLibrary(newValue) {
      this.$emit("update-video-library-status", newValue);
    },
  },

  async mounted() {
    // Initial load / context hydration:
    // - If isEditBulkFormat: jump directly to player settings.
    // - If creating: copy campaignCreation from store, set default formats.
    // - If Product Pages: fetch full products to resolve product-based CTAs.
    // - Hydrate videos (add missing metadata) from video library if needed.
    // - Merge product-attached videos (Product Pages) into videosToIterate.
    this.loadContent = true;
    if (this.isEditBulkFormat) {
      this.playerSelected = true;
    }
    !this.isEditMode && (this.campaignCreation = this.getCampaignCreation());
    if (this.isProductPages) {
      await this.$store.dispatch("product/getProducts", {
        siteId: this.getSiteId,
        isProductListView: false,
      });
    }

    if (this.isEditMode) {
      if (Object.keys(this.singleCampaign() ?? {}).length > 0) {
        if (!this.isBulk) {
          this.campaign = structuredClone(this.singleCampaign());
        } else {
          this.campaign = structuredClone({
            ...structuredClone(this.singleCampaign()),
            videos: !this.isEditBulkFormat
              ? [...this.currentFeedEdit.videos] || []
              : [...this.feedList[0].videos] || [],
          });

          if (this.isEditBulkFormat) {
            this.playerSelected = true;
          }
        }
      } else {
        let campaignId;
        if (this.campaignIdFromProductPages) {
          campaignId = this.campaignIdFromProductPages;
        } else if (this.isProductPages) {
          campaignId = this.$route.query.campaignId;
        } else {
          campaignId = this.$route.params.campaignId;
        }
        const fetchCampaign = await this.$store.dispatch("campaign/getSingle", {
          campaignId: campaignId,
          siteId: this.getSiteId,
        });

        if (!this.isBulk) {
          this.campaign = structuredClone(fetchCampaign.campaign);
        } else {
          this.campaign = structuredClone({
            ...structuredClone(this.singleCampaign()),
            videos: !this.isEditBulkFormat
              ? [...this.currentFeedEdit.videos]
              : [...this.feedList[0].videos],
          });

          if (this.isEditBulkFormat) {
            this.playerSelected = true;
          }
        }
      }

      // Products used in current segmentation (to auto-tag CTAs and update lastImportDate)
      this.productsInSegmentation =
        (this.products?.length &&
          this.products.filter(
            (product) =>
              (this.isBulk &&
                this.currentFeedEdit?.segmentation?.productId?.includes(
                  this.isUsingProductKey
                    ? product.productKey
                    : product.productId
                )) ||
              (!this.isBulk &&
                this.campaign?.segmentation?.productId?.includes(
                  this.isUsingProductKey
                    ? product.productKey
                    : product.productId
                ))
          )) ||
        [];

      // Migrate legacy campaigns: ensure modern fields exist
      this.addPropertiesToLegacyCampaigns();
      // Extract desktop/mobile/shared formats into local state
      this.retrieveFormatsFromCampaign(this.campaign);
    } else if (this.campaignCreation.formats) {
      this.retrieveFormatsFromCampaign(this.campaignCreation);
    } else {
      this.options.forEach((option) =>
        this.setFormatType(option, this.$route.query.goal, true)
      );
      const formats = [];
      if (this.desktop) formats.push({ ...this.desktop });
      if (this.mobile) formats.push({ ...this.mobile });

      this.$store.commit("campaign/campaignCreationSetFormat", {
        formats,
      });
    }
    this.ensureTextOptionsForSingleFormat();

    !this.isEditMode && (this.campaign.videos = this.campaignCreation.videos);
    if (!this.isEditMode && !this.campaign) {
      return;
    }
    if (this.isEditMode && !this.campaignSelected && !this.campaign) {
      return this.$router.push({ name: "campaignManager" });
    }
    if (!this.currentPlan) {
      await this.$store.dispatch("plan/getPlan", {
        siteId: this.getSiteId,
      });
    }

    // If some videos lack metadata (e.g., added by id), hydrate from video library
    const needsHydration =
      Array.isArray(this.campaign?.videos) &&
      this.campaign.videos.some((v) => !this.isVideoHydrated(v));

    if (needsHydration) {
      await this.$store.dispatch("video/getVideoLibrary", {
        siteId: this.getSiteId,
        query: {
          $or: [{ isPremade: { $exists: false } }, { isPremade: false } ],
          $and: [
            {
              fileName: { $exists: true },
              height: { $exists: true },
              width: { $exists: true },
              videoDuration: { $exists: true },
              url: { $not: { $regex: "blob:" } },
            },
          ],
          getAnalytics: true,
        },
      });
    }

    // Merge hydrated metadata into campaign.videos
    this.campaign.videos = this.hydrateFeedVideos(this.campaign.videos);
    // Compute thumbnail grouping fields (carousel)
    this.updateThumbnailGrouping();

    // If we came from Step 1 and chose a non-popup/bubble, show embed code directly (UX)
    if (
      this.comesFromStepOne &&
      this.campaign?.formats[0]?.formatType !== 0 &&
      this.campaign?.formats[0]?.formatType !== 1
    ) {
      this.showEmbedCodeModal = true;
    }

    // If from Product Pages, merge "other feed" videos that should appear on the product
    if (this.videosAttachedOnProduct?.length) {
      this.otherVideosAttachedOnProduct = this.videosAttachedOnProduct.filter(
        (video) => !video.isUniqueProductFeed
      );
    }

    // Resolve product objects in CTA (if only ids are present)
    this.campaign.videos = this.rehydrateAllVideosProducts(
      this.campaign.videos
    );

    // Compute videosToIterate (cards list) considering Product Pages context
    if (this.isProductPages) {
      this.videosToIterate = [
        ...this.campaign.videos,
        ...this.otherVideosAttachedOnProduct,
      ].filter(
        ((video) => {
          const seen = new Set();
          return function (v) {
            if (seen.has(v._id)) return false;
            seen.add(v._id);
            return true;
          };
        })()
      );
    } else this.videosToIterate = this.campaign.videos;

    this.loadContent = false;
  },

  methods: {
    // Toggle leave confirmation modal
    toggleConfirmationModal() {
      this.showConfirmationModal = !this.showConfirmationModal;
    },
    // Leave step 3 and go back to campaign manager
    leaveCampaignCreator() {
      this.$router.push({ name: "campaignManager" });
    },

    // Guard rails for CTA validation (empty fields or invalid timeframe)
    hasUserLeftEmptyAnyCta(cta) {
      if (
        cta &&
        cta.ctaType === CtaType.addToCart &&
        (!cta.products ||
          cta.products.length === 0 ||
          cta.products[0]._id === "")
      ) {
        this.$notify({
          title: this.$t("shared.toastMessage.error"),
          text: this.$t("shared.toastMessage.addProductNoDevice"),
          type: "error",
        });
        this.openAccordion("products");
        return true;
      } else if (
        cta &&
        cta.ctaType !== CtaType.email &&
        cta.ctaType !== CtaType.addToCart &&
        (cta.value === "" || cta.text === "")
      ) {
        this.$notify({
          title: this.$t("shared.toastMessage.error"),
          text: this.$t("shared.toastMessage.emptyButtonField"),
          type: "error",
        });
        this.openAccordion("text");
        return true;
      } else if (
        cta.timeframe &&
        cta.timeframe.fromSec &&
        cta.timeframe.toSec &&
        cta.timeframe.fromSec > cta.timeframe.toSec
      ) {
        this.$notify({
          title: this.$t("shared.toastMessage.error"),
          text: this.$t("shared.toastMessage.validDateRange"),
          type: "error",
        });
        this.openAccordion("timeframe");
        return true;
      }
    },

    // Switch device tab in player settings
    updateDevice(payload) {
      this.chosenDevice = payload;
    },

    // Change any format sub-field (from CampaignFormatSettings)
    changeFormatObject(payload) {
      if ((this.isBubble || this.isPopup) && payload.inputFor === "cta") {
        const newFormatObject = structuredClone(payload);
        this.desktop.cta = newFormatObject.value;
        this.mobile.cta = newFormatObject.value;
      } else {
        let object = this[this.chosenDevice];
        object[payload.inputFor] = payload.value;
      }
    },

    // Update player title (global)
    updateVideoTitle(payload) {
      let object = this[this.chosenDevice];
      object.title = payload;
    },

    // Update per-video CTA (from CTA block)
    changeCtaObject(payload, index = null) {
      if (index == null) index = this.currentVideo?.index;
      if (!this.campaign.videos[index]) return;

      if (payload.ctaType === CtaType.email) {
        delete this.currentVideoCta.ctaAction;
        delete this.currentVideoCta.productIds;
        delete this.currentVideoCta.products;
      }
      // Keep products array if CTA is ATC and we already had selected products
      if (
        this.currentVideoCta?.products &&
        this.currentVideoCta?.ctaType === CtaType.addToCart
      ) {
        payload.products = this.currentVideoCta.products;
        this.currentVideoCta = { ...payload };
      } else {
        this.currentVideoCta = { ...payload };
      }
    },

    // Sticky bar options controls
    changeStickyBarObject(payload) {
      this[this.chosenDevice].stickyBarOptions = { ...payload };
    },

    // Upload/change thumbnail for carousel/embed videos
    changeThumbnail(payload, index = null) {
      if (index == null) index = this.currentVideo?.index;
      this.currentVideo.thumbnail = payload.blob;
      this.currentVideo.tempThumbnailFile = payload.tempThumbnailFile;
      this.embedThumbnail = payload.blob;
    },

    // Update text options for current selected video
    changeText(payload) {
      this.currentVideoTextOptions = payload;
    },

    // Change format type (popup/sticky/embed/bubble/fullscreen-bubble/carousel/stories)
    handleFormatTypeChange(payload) {
      this.setFormatType(this.chosenDevice, payload);
    },

    // Border-color change notification (stories)
    handleBorderColorChanged(payload) {
      this[this.chosenDevice].borderColor = payload;
    },

    // Submit selected products for AddToCart CTA
    submitAddToCartProducts(payload) {
      this.productsLoading = true;
      this.currentVideoCta.products = payload;
      this.productsLoading = false;
      this.showAddToCartModal = false;
      this.$notify({
        title: this.$t("shared.toastMessage.success"),
        text: this.$t("shared.toastMessage.productAdded"),
        type: "success",
      });
    },

    // Abort creation flow, notify GTM (analytics)
    stopCreation() {
      this.$store.commit("campaign/stopCampaignCreation");

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "campaign_create_step3_close",
      });
    },

    // Build a default format object for given device/goal. Called when user switches format.
    setFormatType(device, format, enforceUpdate = false) {
      // Persist title if user had typed one
      this.baseFormatObject.title = this[device].title ?? "";
      let object = !enforceUpdate ? this : this;
      object["desktop"] = this.getDefaultFormatProperties("desktop", format);
      object["mobile"] = this.getDefaultFormatProperties("mobile", format);
      this.isCarousel &&
        !this.campaignAllowedOn.desktop &&
        (this.campaignAllowedOn.desktop = true);
      this.ensureTextOptionsForSingleFormat();
    },

    // Returns a default config object for specific format (per device).
    // NOTE: This method *returns* a new object, it doesn’t mutate state directly.
    getDefaultFormatProperties(device, format) {
      const type =
        this.$route.query.type ?? (this.isFullScreen ? "fullScreen" : "inline");
      switch (format) {
        case "popup":
          this[device].isStickyBar = false;
          return {
            formatType: FormatType.popup,
            deviceType: DeviceType[device],
            ...this.baseFormatObject,
          };
        case "sticky":
          return {
            deviceType: DeviceType[device],
            ...this.baseFormatObject,
            ...this.defaultStickyBarProperties,
          };
        case "embed":
          return {
            deviceType: DeviceType[device],
            ...this.baseFormatObject,
            ...this.defaultEmbedProperties,
            animatedThumbnail: "all",
          };
        case "bubble":
          return {
            deviceType: DeviceType[device],
            ...this.baseFormatObject,
            ...this.defaultBubbleProperties,
            isFullScreen: false,
            openByDefault: false,
            hideBubble: true,
            hideThumbnailPlay: true,
            hideBubbleCloseIcon: false,
          };
        case "fullscreen-bubble":
          this.chosenDevice = "desktop";
          return {
            deviceType: DeviceType[device],
            ...this.baseFormatObject,
            ...this.defaultBubbleProperties,
            hideBubble: true,
            isFullScreen: true,
            openByDefault: false,
            hideBubbleCloseIcon: false,
          };
        case "carousel":
          this.chosenDevice = "desktop";
          if (type === "fullScreen")
            return {
              deviceType: DeviceType[device],
              ...this.baseFormatObject,
              formatType: 3,
              isFullScreen: true,
              carouselThumbnailAmount: 1,
              hideCarouselArrows: false,
              arrowsOverCarousel: true,
              isHorizontalSwipe: false,
              animatedThumbnail: "all",
              thumbnailBorders: {
                isActive: false,
                size: "2",
                color: "#000000",
              },
              thumbnailShadow: false,
            };
          return {
            deviceType: DeviceType[device],
            ...this.baseFormatObject,
            formatType: 3,
            isFullScreen: false,
            hideCarouselArrows: false,
            arrowsOverCarousel: true,
            animatedThumbnail: "all",
            thumbnailBorders: {
              isActive: false,
              size: "2",
              color: "#000000",
            },
            thumbnailShadow: false,
          };
        case "stories":
          this.chosenDevice = "desktop";
          return {
            deviceType: DeviceType[device],
            ...this.baseFormatObject,
            formatType: 3,
            isFullScreen: true,
            isStory: true,
            hideStoryPlay: false,
            borderColor: "#FFA500",
            isHorizontalSwipe: false,
            animatedThumbnail: "all",
            storySize: 80,
            storiesHasBorder: true,
            storiesShape: "round",
          };
      }
    },

    // Legacy safety net: ensure all required properties exist in old campaigns
    addPropertiesToLegacyCampaigns() {
      this.options.forEach((device) => {
        this[device] = { ...this.baseFormatObject, ...this[device] };
        if (this[device].formatType === FormatType.bubble) {
          this[device] = { ...this.defaultBubbleProperties, ...this[device] };
        } else if (this[device].formatType === FormatType.embed) {
          this[device] = { ...this.defaultEmbedProperties, ...this[device] };
        }
      });
      this.campaign.formats = this.campaign.formats.map((deviceFormat) => {
        deviceFormat = { ...this.baseFormatObject, ...deviceFormat };
        if (deviceFormat.formatType === FormatType.bubble) {
          deviceFormat = { ...this.defaultBubbleProperties, ...deviceFormat };
          if (this.campaign.videos.length > 1) {
            deviceFormat.hideBubble = true;
            deviceFormat.hideBubbleCloseIcon = false;
          }
          deviceFormat.isSmallerFrame = false;
          if (deviceFormat.isFullScreen) {
            !deviceFormat.productCardElementsStyle &&
              (deviceFormat.productCardElementsStyle = "square");
          }
        } else if (deviceFormat.formatType === FormatType.embed) {
          deviceFormat = { ...this.defaultEmbedProperties, ...deviceFormat };
        } else if (deviceFormat.formatType === FormatType.carousel) {
          typeof deviceFormat.hideCarouselArrows !== "boolean" &&
            (deviceFormat.hideCarouselArrows = false);

          if (typeof deviceFormat.arrowsOverCarousel !== "boolean") {
            deviceFormat.arrowsOverCarousel = true;
          }
          if (deviceFormat.isFullScreen) {
            !deviceFormat.productCardElementsStyle &&
              (deviceFormat.productCardElementsStyle = "square");
          }

          !deviceFormat.carouselThumbnailAmount &&
            (deviceFormat.carouselThumbnailAmount = 1);

          !deviceFormat.isHorizontalSwipe &&
            (deviceFormat.isHorizontalSwipe = false);
          !deviceFormat.animatedThumbnail &&
            (deviceFormat.animatedThumbnail = "none");
          !deviceFormat.isSmallerFrame && (deviceFormat.isSmallerFrame = false);
        }
        if (
          deviceFormat.isStory &&
          typeof deviceFormat.hideStoryPlay === "undefined"
        ) {
          deviceFormat.hideStoryPlay = false;
        }
        if (
          deviceFormat.isStory &&
          typeof deviceFormat.productCardElementsStyle === "undefined"
        ) {
          !deviceFormat.productCardElementsStyle &&
            (deviceFormat.productCardElementsStyle = "square");
        }
        if (deviceFormat.formatType === 0 && !deviceFormat.popupSize) {
          deviceFormat.popupSize = "medium";
        }
        if (deviceFormat.isStory && !deviceFormat.storySize) {
          deviceFormat.storySize = 80;
        }
        if (deviceFormat.isStory && !deviceFormat.storiesShape) {
          deviceFormat.storiesShape = "round";
        }
        if (
          deviceFormat.isStory &&
          (deviceFormat.storiesHasBorder === undefined ||
            !("storiesHasBorder" in deviceFormat))
        ) {
          deviceFormat.storiesHasBorder = true;
        }
        if (
          (deviceFormat.isStory ||
            deviceFormat.formatType === FormatType.carousel ||
            deviceFormat.formatType === FormatType.bubble) &&
          (deviceFormat.isHorizontalSwipeOnMobile === undefined ||
            !("isHorizontalSwipeOnMobile" in deviceFormat))
        ) {
          deviceFormat.isHorizontalSwipeOnMobile = false;
        }

        return deviceFormat;
      });
    },

    // Auth modal toggle
    toggleSignupModal() {
      this.showSignupModal = !this.showSignupModal;
    },

    // When enabling a new device in UI, copy the other device as starting point
    fillObjectIfEmpty(campaignAllowedOn) {
      this.campaignAllowedOn = campaignAllowedOn;
      let object = this;
      if (this.campaignAllowedOn[this.chosenDevice] && this.isEditMode) {
        if (this.chosenDevice === "desktop") {
          object.desktop = {
            ...this.desktop,
          };
          object.desktop.deviceType = DeviceType.desktop;
        } else {
          object.mobile = {
            ...this.mobile,
          };
          object.mobile.deviceType = DeviceType.mobile;
        }
      }
      if (!this.campaignAllowedOn[this.chosenDevice] && this.currentVideo) {
        this.campaign.videos[this.currentVideo.index].cta = {};
      }
    },

    // Ensure initial state in creation flow
    async init() {
      if (!this.isEditMode) {
        this.$nextTick(async function () {
          if (!this.$store.state.campaign.campaignCreation) {
            return this.$router.push({ name: "campaignManager" });
          }
        });
      }
    },

    // Exit step 3 (creation or edit) and route out
    close() {
      if (this.isEditMode) {
        this.$router.push({ name: "editCampaign" });
      }
      this.$store.commit("campaign/stopCampaignCreation");

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "campaign_create_step3_close",
      });

      this.$router.push({ name: "campaignManager" });
    },

    // Load device formats from a campaign model into local desktop/mobile/shared state
    retrieveFormatsFromCampaign(campaign) {
      if (campaign.formats.length === 2) {
        this.sharedFormat = false;
        this.desktop = {
          ...campaign.formats.find(
            (format) => format.deviceType === DeviceType.desktop
          ),
        };
        this.mobile = {
          ...campaign.formats.find(
            (format) => format.deviceType === DeviceType.mobile
          ),
        };
        this.campaignAllowedOn.desktop = true;
        this.campaignAllowedOn.mobile = true;
        this.chosenDevice = "desktop";
      } else if (campaign.formats.length === 1) {
        this.sharedFormat = true;
        const single = campaign.formats[0];
        this.desktop = single;
        this.mobile = single;
        this.campaignAllowedOn.desktop = true;
        this.campaignAllowedOn.mobile = true;
        this.chosenDevice = "desktop";
      } else if (
        !campaign.formats[0].deviceType ||
        campaign.formats[0].deviceType === DeviceType.desktop
      ) {
        this.sharedFormat = false;
        this.desktop = { ...campaign.formats[0] };
        this.campaignAllowedOn.mobile = false;
        this.campaignAllowedOn.desktop = true;
        this.chosenDevice = "desktop";
      } else {
        this.sharedFormat = false;
        this.mobile = { ...campaign.formats[0] };
        this.campaignAllowedOn.desktop = false;
        this.campaignAllowedOn.mobile = true;
        this.chosenDevice = "mobile";
      }
      this.ensureTextOptionsForSingleFormat();
    },

    // Save formats (and videos when applicable) either in edit mode or creation mode
    async sendFormats() {
      const formats = [];

      // Upload thumbnails for embed/carousel when user changed them
      const desktopToPush = Object.assign({}, this.desktop);
      const mobileToPush = Object.assign({}, this.mobile);

      if (this.isEmbed || this.isCarousel) {
        this.campaign.videos.forEach(async (video) => {
          if (video.thumbnail && video.tempThumbnailFile) {
            let result = uploadThumbnailForVideo(video);
            this.$store.dispatch("video/updateVideoThumbnail", {
              videoId: result._id,
              dataToUpdate: {
                thumbnail: result.thumbnail,
                thumbnailS3Path: result.thumbnailS3Path,
              },
              blobThumbnail: URL.createObjectURL(video.tempThumbnailFile),
            });
          }
        });
        if (!this.isEditMode) {
          this.$store.commit(
            "campaign/campaignCreationSetVideos",
            this.campaign.videos
          );
        }
      }

      if (!this.userDontNeedProducts()) return;

      // If shared format: only one object in formats; otherwise push device-enabled formats separately
      if (this.sharedFormat) {
        const shared = Object.assign({}, this.desktop);
        formats.push(shared);
      } else {
        this.campaignAllowedOn.desktop &&
        desktopToPush &&
        Object.keys(desktopToPush).length
          ? formats.push(desktopToPush)
          : "";
        this.campaignAllowedOn.mobile &&
        mobileToPush &&
        Object.keys(mobileToPush).length
          ? formats.push(mobileToPush)
          : "";
      }

      if (!formats.length) {
        return this.$notify({
          title: this.$t("shared.toastMessage.error"),
          text: this.$t("shared.toastMessage.chooseOneFormat"),
          type: "error",
        });
      }

      const saveTriggerNow = this.shouldSaveTrigger;

      // EDIT MODE SAVE
      if (this.isEditMode) {
        let previousCampaign = this.singleCampaign();

        this.campaign.videos.forEach((video) => {
          video.tempThumbnailFile &&
            (video.thumbnail = URL.createObjectURL(video.tempThumbnailFile));
        });

        const campaignDataToUpdate = { formats };
        if (saveTriggerNow) {
          campaignDataToUpdate.trigger = structuredClone(
            this.campaign.trigger || { type: 0, option: null }
          );
        }

        // If new videos were added, update videoLastImportDate on matching products
        if (this.newVideosAdded) {
          if (this.isBulk) {
            if (
              this.currentFeedEdit.segmentation &&
              Array.isArray(this.currentFeedEdit.segmentation.productId) &&
              this.currentFeedEdit.segmentation.productId.length > 0
            ) {
              await this.updateMatchingProducts(
                this.currentFeedEdit.segmentation.productId
              );
            }
          } else {
            if (
              this.campaign.segmentation &&
              Array.isArray(this.campaign.segmentation.productId) &&
              this.campaign.segmentation.productId.length > 0
            ) {
              await this.updateMatchingProducts(
                this.campaign.segmentation.productId
              );
            }
          }
        }

        // Single (non-bulk) campaign: update videos with formats
        if (!this.campaign.bulk) {
          campaignDataToUpdate.videos = structuredClone(this.campaign.videos);

          await this.$store.dispatch(
            "campaign/updateSingleCampaign",
            campaignDataToUpdate
          );
          this.$emit("campaign-updated", structuredClone(this.campaign));
        }

        // Bulk campaign:
        if (this.campaign.bulk) {
          if (this.isEditBulkFormat) {
            await this.$store.dispatch(
              "campaign/updateSingleCampaign",
              campaignDataToUpdate
            );

            await this.$store.dispatch("campaign/updateCampaign", {
              campaignId: this.campaign._id,
              dataToUpdate: {
                formats,
                ...(saveTriggerNow
                  ? {
                      trigger: structuredClone(
                        this.campaign.trigger || { type: 0, option: null }
                      ),
                    }
                  : {}),
              },
            });
            return this.$emit("close-elements-modal");
          } else {
            this.$store.commit("campaign/setCurrentFeedEdit", {
              ...this.currentFeedEdit,
              videos: this.campaign.videos,
              modificationDate: new Date().toISOString(),
            });

            this.$emit(
              "update-product",
              this.selectedProductId,
              this.videosToIterate
            );

            this.tagProductsToVideos();
            this.newVideosAdded = null;

            this.$notify({
              title: this.$t("shared.toastMessage.success"),
              text: this.$t("create.bulk.notification.bulkSaved"),
              type: "success",
            });

            return this.$emit("update-current-feed");
          }
        }

        // Non-bulk full update
        await this.$store.dispatch("campaign/updateCampaign", {
          campaignId: this.campaign._id,
          dataToUpdate: {
            formats,
            videos: this.campaign.videos,
            ...(saveTriggerNow
              ? {
                  trigger: structuredClone(
                    this.campaign.trigger || { type: 0, option: null }
                  ),
                }
              : {}),
          },
        });

        let newCampaign = this.singleCampaign();
        if (
          this.isEmbed ||
          this.shouldRedirectToEmbedCode(
            previousCampaign.formats[0],
            newCampaign.formats[0]
          )
        ) {
          this.$notify({
            title: this.$t("shared.toastMessage.success"),
            text: this.$t("shared.toastMessage.campaignUpdatedCopyCode"),
            type: "success",
          });
          this.$emit("edit-go-back");
        } else {
          this.$notify({
            title: this.$t("shared.toastMessage.success"),
            text: this.$t("shared.toastMessage.campaignUpdated"),
            type: "success",
          });
          this.$emit("edit-go-back");
        }
      } else {
        // CREATION MODE SAVE (Step 3 -> Step 4 or finalize for embed/carousel)
        await this.$store.commit("campaign/campaignCreationSetFormat", {
          formats,
        });
        if (saveTriggerNow) {
          this.$store.commit("campaign/campaignCreationMerge", {
            trigger: structuredClone(
              this.campaign.trigger || { type: 0, option: null }
            ),
          });
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "campaign_create_step3_next" });

        if (this.isEmbed || this.isCarousel) {
          this.campaign.formats = formats;
          if (this.campaign.formats.length > 1) {
            this.campaign.formats.pop();
          }
          this.addSiteIdToCampaignCreation();
          if (
            this.getUploadVideos.videosToUpload >
            this.getUploadVideos.videosUploaded
          ) {
            this.showUploadProgress = true;
          } else {
            this.saveCampaignToDb();
          }
        } else {
          this.$router.push({ name: "createCampaignStep4" });
        }
      }
    },

    // Decide if we should show embed code prompt after a format change
    shouldRedirectToEmbedCode(oldFormat, newFormat) {
      return (
        (oldFormat.formatType !== newFormat.formatType ||
          oldFormat.isStory !== newFormat.isStory ||
          oldFormat.carouselThumbnailAmount !== newFormat.carouselThumbnailAmount) &&
        (newFormat.formatType === FormatType.carousel ||
          newFormat.isStory === true)
      );
    },

    // In preview: ensure products exist when CTA type is AddToCart
    userDontNeedProducts() {
      return ["desktop", "mobile"]
        .map((device) => {
          if (
            this.currentVideoCta &&
            this.currentVideoCta.ctaType === CtaType.addToCart &&
            this.currentVideoCta.products &&
            this.currentVideoCta.products[0]._id === ""
          ) {
            this.notifyUserHasNoProduct(device);
            return false;
          }
          return true;
        })
        .every((el) => el);
    },

    // Helper to alert users they need to select products for ATC CTA
    notifyUserHasNoProduct(device) {
      this.showAddToCartModal = true;
      this.chosenDevice = device;
      this.$notify({
        title: this.$t("shared.toastMessage.error"),
        text: this.$t("shared.toastMessage.addProduct") + " " + device,
        type: "error",
      });
    },

    // Edit flow: go back to previous screen
    editGoBack() {
      this.$emit("edit-go-back");
    },

    // Creation flow: go back to Step 1
    async goPreviousStep() {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "campaign_create_step3_previous",
      });

      this.$router.push({
        name: "createCampaignStep1",
      });
    },

    // Save & advance:
    // - Validates at least one video exists (except for bulk special cases)
    // - Delegates to sendFormats()
    async goNextStep() {
      this.isLoading = true;
      if (!this.isBulk && this.campaign.videos.length === 0) {
        this.$notify({
          title: this.$t("shared.toastMessage.error"),
          text: "First you need to add at least one video to your campaign",
          type: "error",
        });
      } else {
        await this.sendFormats();
      }
      this.isLoading = false;
    },

    // Remove a video from campaign; maintain grouping invariants
    deleteVideo(index) {
      this.campaign.videos.splice(index, 1);
      if (
        !this.campaign.videos[0]?.isMainGrouping &&
        this.campaign.videos[0]?.thumbnailGrouping
      ) {
        const video = this.campaign.videos[0];
        this.setAsMainThumbnail(video);
        this.$notify({
          title: "Success",
          text: this.$t("create.step3.thumbnailGrouping.deleteFirstThumbnail"),
          type: "success",
        });
      }
    },

    // Close per-video editor; if saveState=true, persist CTA/text/thumbnail into campaign.videos
    deselectVideo(saveState, video) {
      this.isBackButtonClicked = true;
      if (saveState) {
        if (
          this.currentVideoCta.timeframe &&
          this.currentVideoCta.timeframe.fromSec === 0 &&
          this.currentVideoCta.timeframe.toSec === 0
        ) {
          delete this.currentVideoCta.timeframe;
        }
        if (this.hasUserLeftEmptyAnyCta(this.currentVideoCta)) return;
        this.$store.commit("video/updateVideo", { video });
        this.campaign.videos[this.currentVideo.index].cta = JSON.parse(
          JSON.stringify(this.currentVideoCta)
        );
        this.campaign.videos[this.currentVideo.index].textOptions = {
          ...this.campaign.videos[this.currentVideo.index].textOptions,
          ...this.currentVideoTextOptions,
        };
        if (
          !this.currentVideoTextOptions ||
          this.currentVideoTextOptions.text === ""
        ) {
          delete this.campaign.videos[this.currentVideo.index].textOptions;
        }
        if (this.currentVideo.tempThumbnailFile) {
          this.campaign.videos[this.currentVideo.index].thumbnail =
            this.currentVideo.thumbnail;

          this.campaign.videos[this.currentVideo.index].tempThumbnailFile =
            this.currentVideo.tempThumbnailFile;
        }
      }
      this.currentVideo = null;
      this.currentVideoCta = {};
      this.embedThumbnail = "";
    },

    // Close player settings view; if saveState=true, persist formats into store
    deselectPlayer(saveState) {
      this.isBackButtonClicked = true;
      if (!this.getAllowedCampaign) {
        this.$notify({
          title: this.$t("shared.toastMessage.error"),
          text: this.$t("shared.toastMessage.chooseOneFormat"),
          type: "error",
        });
        return;
      }
      if (saveState) {
        const formats = [];
        if (this.isEditMode) {
          const formats = [];
          if (this.desktop) formats.push({ ...this.desktop });
          if (this.mobile) formats.push({ ...this.mobile });

          const payload = {
            formats,
            ...(this.shouldSaveTrigger
              ? {
                  trigger: structuredClone(
                    this.campaign.trigger || { type: 0, option: null }
                  ),
                }
              : {}),
          };
          this.$store.dispatch("campaign/updateSingleCampaign", payload);
        } else {
          this.$store.commit("campaign/campaignCreationSetFormat", {
            formats: [{ ...this.desktop }, { ...this.mobile }],
          });
        }
      } else {
        this.campaign = this.isEditMode
          ? structuredClone(this.singleCampaign())
          : structuredClone(this.getCampaignCreation());
        this.retrieveFormatsFromCampaign(this.campaign);
      }
      this.playerSelected = false;
    },

    // Apply the current CTA to all videos in the campaign
    copyCtaForAllVideos() {
      if (this.hasUserLeftEmptyAnyCta(this.currentVideoCta)) return;
      this.campaign.videos.forEach((video) => {
        video.cta = this.currentVideoCta;
      });
      this.$notify({
        title: this.$t("shared.toastMessage.success"),
        text: this.$t("create.step3.videoElementCopied"),
        type: "success",
      });
    },

    // Helper to open the proper accordion panel in CTA editor
    openAccordion(type) {
      let element = document.querySelector(`#accordion-${type}`).children[0];
      if (!element.ariaExpanded) element.click();
    },

    // Keep products order for AddToCart CTA
    handleProductOrderChange(products) {
      this.currentVideoCta.products = [...products];
    },

    // Preview toggles fullscreen (update local flag)
    handleFullscreenToggle(value) {
      this.isCurrentlyFullscreen = value;
    },

    // Open modal to nominate a video as "main grouping thumbnail"
    openGroupingThumbnailModal(video) {
      this.showGroupingThumbnailModal = true;
      this.videoToGroupInThumbnail = video;
    },

    // Mark a video as main grouping (affects thumbnailGrouping labelling)
    setAsMainThumbnail(video) {
      const index = this.campaign.videos.indexOf(video);

      if (index !== 0 && !this.campaign.videos[0].isMainGrouping) {
        this.$notify({
          title: "Error",
          text: this.$t("create.step3.thumbnailGrouping.firstThumbnail"),
          type: "error",
        });
        return;
      }

      this.$set(video, "isMainGrouping", true);
      this.$store.dispatch("video/updateVideo", {
        video: video,
      });

      this.updateThumbnailGrouping();
      this.$notify({
        title: "Success",
        text: this.$t("create.step3.thumbnailGrouping.thumbnailSet"),
        type: "success",
      });
    },

    // Remove "main grouping" from a video (enforce invariant for index 0)
    disableMainThumbnail(video) {
      const index = this.campaign.videos.indexOf(video);
      const isFirstVideoOnlyMainThumbnail =
        this.campaign.videos.filter((v) => v.isMainGrouping).length === 1;

      if (index === 0 && !isFirstVideoOnlyMainThumbnail) {
        this.$notify({
          title: "Error",
          text: this.$t("create.step3.thumbnailGrouping.removeFirst"),
          type: "error",
        });
        return;
      }
      this.$delete(video, "isMainGrouping");

      this.$store.dispatch("video/updateVideo", {
        video: video,
      });

      this.updateThumbnailGrouping();
      this.$notify({
        title: "Success",
        text: this.$t("create.step3.thumbnailGrouping.thumbnailRemoved"),
        type: "success",
      });
    },

    // Recalculate thumbnailGrouping labels after reorder or toggles
    updateThumbnailGrouping() {
      let nextGroupingNumber = 1;
      let previousGroupingNumber = 0;

      for (let i = 0; i < this.campaign.videos.length; i++) {
        const video = this.campaign.videos[i];

        if (!this.hasMainGrouping) {
          delete video.thumbnailGrouping;
          this.$store.dispatch("video/updateVideo", {
            video: video,
          });
          continue;
        }

        if (video.isMainGrouping) {
          video.thumbnailGrouping = `thumbnail ${nextGroupingNumber}`;
          previousGroupingNumber = nextGroupingNumber;
          nextGroupingNumber++;
        } else {
          video.thumbnailGrouping = `thumbnail ${previousGroupingNumber}`;
        }
        this.$store.dispatch("video/updateVideo", {
          video: video,
        });
      }
    },

    // Handle sortable change event (draggable)
    changeOrder(event) {
      const video = this.campaign.videos[0];

      if (
        (event.moved.newIndex === 0 || event.moved.oldIndex === 0) &&
        !video.isMainGrouping &&
        this.hasMainGrouping
      ) {
        this.$set(video, "isMainGrouping", true);
        this.$store.dispatch("video/updateVideo", {
          video: video,
        });
        this.updateThumbnailGrouping();
        this.$notify({
          title: "Success",
          text: this.$t("create.step3.thumbnailGrouping.fistThumbnail2"),
          type: "success",
        });
      } else {
        this.updateThumbnailGrouping();
      }
    },

    // Close video library modal
    closeVideoLibrary() {
      this.selectedVideo = null;
      this.openVideoLibrary = false;
    },

    // Toggle importer (not used directly here; placeholder for integrations)
    async toggleImporterModal(importer) {
      this.showImporterModal = !this.showImporterModal;
      this.showImporterModal && (this.importUrl = "");
      this.selectedImporter = importer;
    },

    // Add files dropped/pushed by FloatingUploadingBar
    pushFiles(files) {
      this.isLoading = true;
      if (this.newVideosAdded === null) {
        this.newVideosAdded = files;
      } else {
        this.newVideosAdded = [...this.newVideosAdded, ...files];
      }
      files.forEach((file) => {
        // If segmentation targets products, auto-tag new video CTA with those products
        this.addProductCtaToNewVideo(file);

        this.campaign.videos.push(file);
        this.$emit("update-campaign", file);
      });

      this.isLoading = false;
    },

    // Show a library video on the right-side preview
    selectVideoInPreview(video) {
      this.selectedVideo = video;
    },

    // Import chosen library videos into this campaign
    async importVideosFromLibrary(videoArray) {
      if (this.newVideosAdded === null) {
        this.newVideosAdded = videoArray;
      } else {
        this.newVideosAdded = [...this.newVideosAdded, ...videoArray];
      }
      videoArray.forEach(async (videoObject) => {
        if (!videoObject.thumbnail) {
          videoObject.thumbnail = await generateVideoCover(videoObject.url, 4);
          videoObject.thumbnail = URL.createObjectURL(videoObject.thumbnail);
        }
        this.isImageReturningAnError(videoObject.thumbnail).then(
          async (result) => {
            if (!result) {
              const blobFile = await generateVideoCover(videoObject.url, 4);
              videoObject.thumbnail = URL.createObjectURL(blobFile);
              return;
            }
          }
        );

        // Auto-tag CTA with products (if segmentation applies)
        this.addProductCtaToNewVideo(videoObject);

        this.campaign.videos.push(videoObject);
        this.$emit("update-campaign", videoObject);
      });
      this.$store.commit(
        "campaign/updateSingleCampaignVideos",
        this.campaign.videos
      );
      this.openVideoLibrary = false;
    },

    // Validate image (thumbnail) URL (fallback to generating one if broken)
    isImageReturningAnError(thumbnail) {
      return new Promise((resolve) => {
        let img = new Image();
        img.onload = () => {
          resolve(true);
        };
        img.onerror = () => {
          resolve(false);
        };
        img.src = thumbnail;
      });
    },

    // If products are segmented, prefill CTA with those products for new videos
    addProductCtaToNewVideo(video) {
      if (!this.productsInSegmentation.length) return;

      const productIds = this.productsInSegmentation.map(
        (product) => product._id
      );

      video.cta = {
        ...this.defaultCtaObject,
        productIds,
        products: this.productsInSegmentation,
      };
    },

    // After saving bulk changes, tag the new videos with product ids in DB
    async tagProductsToVideos(productId) {
      if (!this.newVideosAdded?.length) return;

      for (const video of this.newVideosAdded) {
        const currentProductIds = video.productIds || [];

        const updatedProductIds = [
          ...new Set([
            ...currentProductIds,
            ...this.productsInSegmentation.map((product) => product._id),
          ]),
        ];

        await this.$store.dispatch("video/updateVideoProductIds", {
          videoId: video._id,
          productIds: updatedProductIds,
        });
      }
    },

    // Save bulk feed list back to campaign
    async saveCampaign(campaignId) {
      await this.$store.dispatch("campaign/updateCampaign", {
        campaignId: campaignId,
        dataToUpdate: {
          bulk: this.feedList,
        },
      });
      this.$notify({
        title: this.$t("shared.toastMessage.success"),
        text: this.$t("create.bulk.notification.bulkSaved"),
        type: "success",
      });
    },

    // Close embed code modal
    closeEmbedCodeModal() {
      this.showEmbedCodeModal = false;
    },

    // Update videoLastImportDate for products when new videos were added
    async updateMatchingProducts(productIds) {
      const matchingProducts = this.products.filter((product) =>
        productIds.includes(
          this.isUsingProductKey ? product.productKey : product.productId
        )
      );
      for (const product of matchingProducts) {
        const now = new Date().toISOString();
        try {
          await this.$store.dispatch("product/updateProduct", {
            productId: product._id,
            productData: {
              videoLastImportDate: now,
            },
          });
        } catch (error) {
          console.error("Error updating product:", error);
        }
      }
    },

    // Manual index change from a child event
    handleChangeIndex({ oldIndex, newIndex }) {
      const movedVideo = this.campaign.videos.splice(oldIndex, 1)[0];
      this.campaign.videos.splice(newIndex, 0, movedVideo);
      const video = this.campaign.videos[0];

      this.$nextTick(() => {
        this.$forceUpdate();
      });
      if (
        (newIndex === 0 || oldIndex === 0) &&
        !video.isMainGrouping &&
        this.hasMainGrouping
      ) {
        this.$set(video, "isMainGrouping", true);
        this.$store.dispatch("video/updateVideo", {
          video: video,
        });
        this.updateThumbnailGrouping();
        this.$notify({
          title: "Success",
          text: this.$t("create.step3.thumbnailGrouping.fistThumbnail2"),
          type: "success",
        });
      } else {
        this.updateThumbnailGrouping();
      }
    },

    // Remove a single product from AddToCart CTA
    removeProduct(id) {
      const index = this.currentVideoCta.products.findIndex(
        (product) => product._id === id
      );
      this.currentVideoCta.products.splice(index, 1);
    },

    // Reset library preview selection
    resetSelectedVideo() {
      this.selectedVideo = null;
    },

    // Open replace video modal
    openReplaceVideoModal(index, video) {
      this.videoIndexToReplace = index;
      this.videoToReplace = video;
      this.showReplaceVideoModal = true;
    },

    // Close replace video modal
    closeReplaceVideoModal() {
      this.showReplaceVideoModal = false;
      this.videoIndexToReplace = null;
    },

    // Replace video in the list (same index)
    replaceVideo({ index, newVideo }) {
      this.closeReplaceVideoModal();
      this.campaign.videos[index] = newVideo;
      this.campaign.videos = [...this.campaign.videos];
    },

    // When in bulk mode, gray-out or lock videos coming from other feeds or multi-product feeds
    isNotCurrentFeed(video) {
      if (!this.campaign.bulk) {
        return false;
      }

      if (video.isUniqueProductFeed !== undefined) {
        return video.isUniqueProductFeed === false;
      }

      const isSameFeedIndex = video.feedIndex === this.currentFeedEditIndex;
      const hasMultipleProducts =
        this.campaign.bulk[video.feedIndex]?.segmentation?.productId?.length >
        1;

      return (
        (!isSameFeedIndex || hasMultipleProducts) &&
        video.feedIndex !== undefined
      );
    },

    // For draggable: mark non-draggable cards (videos belonging to other feeds)
    nonDraggable(video) {
      return video.feedIndex !== undefined;
    },

    // DRAG & DROP: show overlay if files are dragged into window
    handleDragOver(event) {
      if (this.hasAnyModalOpen) return;
      if (event.dataTransfer.types.includes("Files")) {
        event.preventDefault();
        if (!this.isDragging) {
          this.isDragging = true;
        }
      }
    },

    // Hide overlay when pointer leaves the component bounds
    handleDragLeave(event) {
      if (this.hasAnyModalOpen) return;
      const rect = event.currentTarget.getBoundingClientRect();
      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      ) {
        this.isDragging = false;
      }
    },

    // Handle files drop and pass them to FloatingUploadingBar
    handleDrop(event) {
      if (this.hasAnyModalOpen) return;
      if (event.dataTransfer.files.length > 0) {
        event.preventDefault();
        this.isDragging = false;
        this.filesToUpload = {
          type: "drop",
          dataTransfer: event.dataTransfer,
        };
      }
    },

    // Merge minimal video objects (e.g., only id) with full metadata from library
    hydrateFeedVideos(list) {
      if (!Array.isArray(list)) return list;

      const pool =
        (Array.isArray(this.getVideoLibrary) && this.getVideoLibrary) ||
        (Array.isArray(this.getVideos) && this.getVideos) ||
        [];

      const byId = new Map(pool.map((v) => [v._id, v]));

      const needsHydration = list.some(
        (v) => v && !v._id && (v.videoId || v.id)
      );

      if (!needsHydration) return list;

      return list.map((v) => {
        if (!v) return v;
        const id = v._id || v.videoId;
        const full = byId.get(id);
        if (!full) return v;
        return { ...full, ...v, _id: full._id };
      });
    },

    // Basic check to see if a video object has enough metadata
    isVideoHydrated(v) {
      if (!v) return false;
      return Boolean(v.videoDuration && v.fileName);
    },

    // Rebuild CTA.products array if only productIds were persisted
    resolveCtaProducts(cta) {
      if (!cta || cta.ctaType !== this.CtaType.addToCart) return cta;

      if (Array.isArray(cta.products) && cta.products.length) {
        const valid = cta.products.filter((p) => p && p._id && p._id !== "");
        if (valid.length) {
          cta.productIds = valid.map((p) => p._id);
        }
      }

      const hasValidProducts =
        Array.isArray(cta.products) &&
        cta.products.length > 0 &&
        cta.products[0]?._id &&
        cta.products[0]._id !== "";

      if (!hasValidProducts) {
        const ids = Array.isArray(cta.productIds) ? cta.productIds : [];
        if (
          ids.length &&
          Array.isArray(this.products) &&
          this.products.length
        ) {
          const byId = new Map(this.products.map((p) => [p._id, p]));
          cta.products = ids.map((id) => byId.get(id)).filter(Boolean);
        }
      }

      return cta;
    },

    // Apply resolveCtaProducts to the full list
    rehydrateAllVideosProducts(videos) {
      if (!Array.isArray(videos)) return videos;
      return videos.map((v) => {
        if (v?.cta) v.cta = this.resolveCtaProducts({ ...v.cta });
        return v;
      });
    },

    // Ensure textOptions exist for single shared format cases (copy from first video if missing)
    ensureTextOptionsForSingleFormat() {
      const formats = this.campaign?.formats || [];
      const singleFormat =
        formats.length === 1 ||
        [
          this.campaignAllowedOn?.desktop,
          this.campaignAllowedOn?.mobile,
        ].filter(Boolean).length === 1;

      if (!singleFormat) return;

      const fmt = formats[0] || this.selectedFormatObject;
      const isCarousel = fmt?.formatType === this.FormatType.carousel;
      const isEmbed = fmt?.formatType === this.FormatType.embed;
      const isStories = Boolean(fmt?.isStory);

      if (!(isCarousel || isEmbed || isStories)) return;

      const targetDevice =
        fmt?.deviceType === this.DeviceType.desktop
          ? "desktop"
          : fmt?.deviceType === this.DeviceType.mobile
          ? "mobile"
          : this.getAllowedCampaign;

      if (!targetDevice || !this[targetDevice]) return;

      const empty =
        !this[targetDevice].textOptions ||
        Object.keys(this[targetDevice].textOptions).length === 0;

      if (empty) {
        const fromFirstVideo = this.campaign?.videos?.[0]?.textOptions;
        const value =
          fromFirstVideo && Object.keys(fromFirstVideo).length > 0
            ? { ...fromFirstVideo }
            : {};
        this.$set(this[targetDevice], "textOptions", value);
      }
    },
  },
};
</script>

<style lang="scss">
.video-library-modal .sliding-modal {
  min-width: 408px;
  padding: 20px 0;
  margin-left: 0;
}
</style>

<style lang="scss" scoped>
$format-step-width: 40%;
$format-step-min-width: 400px;
$format-step-max-width: 525px;

.step3-wrapper {
  width: 100%;
  height: 100vh;
  position: relative;
}

.close-icon {
  top: 10px;
  right: 10px;
  background: none;
  position: absolute;
  z-index: 102;
}

.bulk-preview {
  width: 75%;
}

.slide-from-left {
  animation-name: slideFromLeft;
  animation-duration: 0.5s;
  animation-timing-function: ease-out;
}
.bulk-slide-from-left {
  animation: none;
}

.bulk-slide-from-left::-webkit-scrollbar {
  width: 0;
}

.bulk-slide-from-left::-webkit-scrollbar-track {
  background-color: transparent;
}

.bulk-slide-from-left::-webkit-scrollbar-thumb {
  background-color: transparent;
}

@keyframes slideFromLeft {
  0% {
    transform: translateX(-100%);
    opacity: 1;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

.slide-from-right {
  animation-name: slideFromRight;
  animation-duration: 0.5s;
  animation-timing-function: ease-out;
}

@keyframes slideFromRight {
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

.bubble-size-wrapper {
  display: flex;
  gap: 4px;
  color: $dark-purple;
}

.bubble-size-input {
  display: flex;
  justify-content: space-between;
}

.preview {
  background-color: rgba(226, 124, 252, 0.1);
}

.step-navbar {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 40%;
  box-sizing: border-box;
  height: 100%;
  .video-cards {
    display: flex;
    flex-direction: column;
    margin-right: auto;
    margin-left: auto;
    height: 100%;
    overflow-y: scroll;
    margin-top: 24px;
  }
  .bulk-video-cards {
    margin: 0px 10px 0px 10px;
    overflow: auto;
  }
  .bulk-video-cards::-webkit-scrollbar {
    width: 0;
  }

  .bulk-video-cards::-webkit-scrollbar-track {
    background-color: transparent;
  }

  .bulk-video-cards::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
  h1 {
    @include font-big;
    font-weight: $w-medium-bolder;
    margin: 0 20px 16px;
  }
}
.bulk-video-card {
  width: 95%;
}
.bulk-video-card:last-child {
  margin-bottom: 130px;
}
.bulk-step-navbar {
  width: 25%;
}
.cta-navbar {
  position: relative;
  width: 40%;
  display: flex;
  flex-direction: column;
  text-align: center;
}
.bulk-cta-navbar {
  width: 25%;
  min-width: 300px;
}

.back-button {
  @include base-font;
  color: #585858;
  display: flex;
  gap: 5px;
  background: white;
  margin: 1rem 0;
  padding: 0;
  path {
    fill: black;
  }
  p {
    font-size: 14px;
    font-weight: 500;
  }
}

.widget-creator-back-button {
  margin-top: 0;
}

.cta-wrapper {
  overflow-y: auto;
  height: 100%;
  padding: 16px 0;
  @include flex-col-gap(16px);
  margin-bottom: 60px;
}

.cta-wrapper::-webkit-scrollbar {
  width: 0;
}

.cta-wrapper::-webkit-scrollbar-track {
  background-color: transparent;
}

.cta-wrapper::-webkit-scrollbar-thumb {
  background-color: transparent;
}

.general-player-button {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  background: #ffffff;
  box-shadow: 0px 0px 6px 0.5px rgba(0, 0, 0, 0.4);
  border-radius: 4px;
  margin: 20px auto;
  padding: 20px;
  align-items: center;
  cursor: pointer;
  width: 90%;
  box-sizing: border-box;
  &:hover {
    box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.5);
  }
  img {
    height: 24px;
    width: 14px;
  }
  .player-settings > p {
    color: $medium-grey;
    @include font-smaller;
    margin-top: 10px;
  }
}
.edit-general-player-button {
  margin-top: 24px;
}

.done-button-wrapper {
  background: white;
  z-index: 1;
  margin-top: auto;
}
.bulk-done-button {
  margin-bottom: 40px;
}
.widget-creator-done-button {
  margin-bottom: 30px;
}

.edit-mode-done-button {
  margin-bottom: 40px;
}

.product-pages-done-button {
  margin-bottom: 40px;
}
.done-button {
  cursor: pointer;
  text-align: center;
  background: $dark-purple;
  padding: 15px;
  color: white;
  @include media("<=tablet") {
    margin-bottom: 30px;
  }
}
.formatStep {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
}

.title-wrapper {
  margin: 0 auto;
  width: 85%;
}
.bulk-title-wrapper {
  margin-top: 24px;
  margin-bottom: 0;
}
.player-title-wrapper {
  margin: 16px auto;
  width: 90%;
}

.widget-creator-title-wrapper {
  margin: 0 auto;
}

.edit-mode-title-wrapper {
  margin: 24px auto 0;
}
.vertical-margin {
  margin-top: 35px;
  margin-left: 60px;
}
.text-center h2 {
  text-align: center;
}

.title-bolder {
  font-weight: $w-medium-bolder !important;
}

.step-title {
  text-align: start;
  @include font-big;
  .bold {
    font-weight: $w-bolder;
  }
}

.settings {
  max-width: 525px;
  width: 40%;
  height: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
}

.bulk-settings {
  width: 25%;
}

.deactivate-thumbnail {
  pointer-events: none;
  filter: grayscale(1);
}

.vidjet-radio-button.toggle {
  line-height: 25px;
}
.confirmation-modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.confirmation-modal-title {
  @include base-font;
  @include font-big;
  font-weight: 600;
  margin-bottom: 14px;
}
.confirmation-modal-subtitle {
  @include base-font;
  @include font-normal;
  font-weight: 400;
  margin-bottom: 24px;
  white-space: nowrap;
}
.confirmation-modal-buttons {
  margin-top: 36px;
  display: flex;
  gap: 85px;
}
// .back-button {
//   box-shadow: 2px 2px 15px 0px rgba(0, 0, 0, 0.18);
//   &:hover {
//     background-color: rgba(0, 0, 0, 0.18);
//   }
// }

.modal-background {
  z-index: map-get($zindex, modal);
  width: 45%;
  background-color: transparent;
}

.no-videos-section {
  height: calc(100% - 50px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 36px;
}

.no-products-images {
  position: relative;
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

.absolute-image {
  position: absolute;
  right: 10px;
  top: 0;
}

.no-products-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

.main-text {
  @include base-font;
  font-weight: 600;
}

.secondary-text {
  @include base-font;
  font-weight: 600;
  color: $medium-grey;
  @include font-smaller;
}

.edit-bulk-settings-footer-wrapper {
  position: absolute;
  right: 110px;
  z-index: 1000;
  top: 61px;
}

.edit-settings-footer-wrapper {
  position: absolute;
  right: 16px;
  top: 12px;
  z-index: 1000;
}

.flex-button {
  display: flex;
  align-items: center;
  gap: 8px;
}

.absolute-position {
  position: absolute;
  right: 16px;
  top: 12px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.bulk-absolute-position {
  top: -15px;
}
.product-pages-absolute-position {
  top: -25px;
}
.cancel-button {
  &:hover {
    .arrow-back {
      path {
        fill: $dark-purple;
      }
    }
  }
}
.arrow-back {
  transform: rotate(180deg);
  path {
    fill: $light-grey;
  }
}

.bulk-format-footer-settings {
  position: absolute;
  top: 61px;
  right: 103px;
  z-index: 1000;
}

.upload-full-screen-zone {
  position: fixed;
  top: 0;
  right: 0;
  width: 90%;
  height: 100%;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(123, 1, 181, 0.2);
  border: 4px dashed $dark-purple;
  box-sizing: border-box;
  p {
    @include base-font;
    @include font-normal;
    font-weight: 700;
    color: white;
  }
  @media (max-width: 1200px) {
    width: 95%;
  }
}
</style>
